import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { tradeLicenseApi } from '../../services/tradeLicenseApi';
import ListPagination from '../../components/Office/ListPagination';
import './AuditLogs.css';

const PAGE_SIZE = 20;

const ACTION_OPTIONS = [
  'application_created',
  'application_submitted',
  'verification_started',
  'document_verified',
  'clarification_requested',
  'application_approved',
  'application_rejected',
  'payment_received',
  'license_issued',
  'certificate_generated',
];

const labelize = (value) => (value || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    action: '',
    search: '',
    date_from: '',
    date_to: ''
  });
  const [selectedLog, setSelectedLog] = useState(null);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ count: 0, next: null, previous: null });

  const fetchAuditLogs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await tradeLicenseApi.getAuditLogs({
        ...filters,
        ordering: '-performed_date',
        page,
        page_size: PAGE_SIZE,
      });
      const data = response.data;
      setLogs(data.results || data);
      setPagination({
        count: data.count ?? (Array.isArray(data) ? data.length : 0),
        next: data.next ?? null,
        previous: data.previous ?? null,
      });
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
      setLogs([]);
      setPagination({ count: 0, next: null, previous: null });
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => {
    fetchAuditLogs();
  }, [fetchAuditLogs]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = () => {
    setPage(1);
    fetchAuditLogs();
  };

  const handleClearFilters = () => {
    setFilters({ action: '', search: '', date_from: '', date_to: '' });
    setPage(1);
  };

  const exportLogs = () => {
    const rows = logs.map((log) => ({
      date: log.performed_date,
      action: labelize(log.action),
      user: log.performed_by_name || 'System',
      application: log.application_number || '',
      license: log.license_number || '',
      remarks: log.remarks || '',
    }));
    const csv = [
      'Date,Action,User,Application,License,Remarks',
      ...rows.map((row) => Object.values(row).map((value) => `"${String(value).replace(/"/g, '""')}"`).join(',')),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'trade_audit_logs.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="audit-logs-container">
      <h1 className="page-title">Audit Logs</h1>

      <div className="audit-filters">
        <h3>Filters</h3>
        <div className="filter-row">
          <div className="filter-group">
            <label>Action:</label>
            <select
              name="action"
              value={filters.action}
              onChange={handleFilterChange}
            >
              <option value="">All Actions</option>
              {ACTION_OPTIONS.map((action) => (
                <option key={action} value={action}>{labelize(action)}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Search:</label>
            <input
              type="text"
              name="search"
              value={filters.search}
              onChange={handleFilterChange}
              placeholder="Application, license, user, remarks"
            />
          </div>

          <div className="filter-group">
            <label>From Date:</label>
            <input
              type="date"
              name="date_from"
              value={filters.date_from}
              onChange={handleFilterChange}
            />
          </div>

          <div className="filter-group">
            <label>To Date:</label>
            <input
              type="date"
              name="date_to"
              value={filters.date_to}
              onChange={handleFilterChange}
            />
          </div>
        </div>

        <div className="filter-actions">
          <button className="btn btn-primary" onClick={handleApplyFilters}>
            Apply Filters
          </button>
          <button className="btn btn-secondary" onClick={handleClearFilters}>
            Clear Filters
          </button>
          <button className="btn btn-secondary" onClick={exportLogs} disabled={logs.length === 0}>
            Export CSV
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading audit logs...</div>
      ) : logs.length > 0 ? (
        <>
        <div className="audit-logs-list">
          {logs.map((log) => (
            <div key={log.id} className="audit-log-item">
              <div className="log-header">
                <strong>{labelize(log.action)}</strong>
                <span className="log-date">
                  {new Date(log.performed_date).toLocaleString()}
                </span>
              </div>
              <div className="log-details">
                <p><strong>User:</strong> {log.performed_by_name || 'System'}</p>
                <p>
                  <strong>Application:</strong>{' '}
                  {log.application ? (
                    <Link to={`/office/applications/${log.application}`}>{log.application_number}</Link>
                  ) : (log.application_number || 'N/A')}
                </p>
                <p><strong>License:</strong> {log.license_number || 'N/A'}</p>
                <p><strong>IP Address:</strong> {log.ip_address || 'N/A'}</p>
                {log.remarks && <p><strong>Remarks:</strong> {log.remarks}</p>}
              </div>
              {(log.old_values || log.new_values) && (
                <button
                  className="btn btn-secondary btn-small"
                  onClick={() => setSelectedLog(log)}
                >
                  View Changes
                </button>
              )}
            </div>
          ))}
        </div>
        <ListPagination
          count={pagination.count}
          next={pagination.next}
          previous={pagination.previous}
          currentPage={page}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
          loading={loading}
        />
        </>
      ) : (
        <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
          <p>No audit logs found</p>
        </div>
      )}

      {/* Changes Modal */}
      {selectedLog && (
        <div className="modal-overlay" onClick={() => setSelectedLog(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Changes Details</h2>

            {selectedLog.old_values && Object.keys(selectedLog.old_values).length > 0 && (
              <div className="changes-section">
                <h3>Old Values</h3>
                <dl className="detail-list">
                  {Object.entries(selectedLog.old_values).map(([key, value]) => (
                    <React.Fragment key={key}>
                      <dt>{key}:</dt>
                      <dd>{JSON.stringify(value)}</dd>
                    </React.Fragment>
                  ))}
                </dl>
              </div>
            )}

            {selectedLog.new_values && Object.keys(selectedLog.new_values).length > 0 && (
              <div className="changes-section">
                <h3>New Values</h3>
                <dl className="detail-list">
                  {Object.entries(selectedLog.new_values).map(([key, value]) => (
                    <React.Fragment key={key}>
                      <dt>{key}:</dt>
                      <dd>{JSON.stringify(value)}</dd>
                    </React.Fragment>
                  ))}
                </dl>
              </div>
            )}

            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setSelectedLog(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLogs;